import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Users, Wrench, UserCircle, BarChart3, Ship, LogOut, Shield, Euro, Clock, Calendar, Settings as SettingsIcon, Bell, BellOff } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { requestPushPermission, currentPushToken } from "@/lib/push";

const AVATAR_FALLBACK =
  "https://api.dicebear.com/7.x/initials/svg?seed=";

export default function Perfil() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  // Estado del sistema de push
  const [pushEnabled, setPushEnabled] = useState(false);         // backend has Firebase configured
  const [pushPermission, setPushPermission] = useState("default"); // 'granted' | 'denied' | 'default'
  const [pushHasToken, setPushHasToken] = useState(!!currentPushToken());
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState("");

  useEffect(() => {
    // Sondear estado del backend + del navegador
    if (typeof Notification !== "undefined") setPushPermission(Notification.permission);
    api.get("/push/status").then((r) => setPushEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);

  const activarPush = async () => {
    setPushBusy(true); setPushMsg("");
    try {
      if (!pushEnabled) {
        setPushMsg(lang === "en"
          ? "Notifications are not configured on the server yet. Contact support."
          : "Las notificaciones aún no están configuradas en el servidor. Contacta soporte.");
        return;
      }
      if (typeof Notification !== "undefined" && Notification.permission === "denied") {
        setPushMsg(lang === "en"
          ? "You blocked notifications in the browser. Open site settings (padlock icon) and allow notifications, then refresh."
          : "Has bloqueado las notificaciones en el navegador. Abre los ajustes del sitio (icono candado) y permítelas, luego refresca.");
        return;
      }
      const res = await requestPushPermission();
      if (res.ok) {
        setPushPermission("granted");
        setPushHasToken(true);
        setPushMsg(lang === "en" ? "Notifications enabled ✓" : "Notificaciones activadas ✓");
      } else {
        const reasons = {
          denied: lang === "en" ? "You blocked notifications in the browser. Enable them in site settings." : "Has bloqueado las notificaciones en el navegador. Actívalas desde los ajustes del sitio.",
          unsupported: lang === "en" ? "This browser doesn't support notifications." : "Este navegador no soporta notificaciones.",
          not_configured: lang === "en" ? "Push service not configured yet." : "El servicio de push aún no está configurado.",
          no_sw: lang === "en" ? "Service Worker unavailable." : "Service Worker no disponible.",
          no_token: lang === "en" ? "Couldn't get a token. Check Firebase VAPID key configuration." : "No se pudo obtener el token. Revisa la configuración VAPID de Firebase.",
          backend_error: lang === "en" ? "Server rejected the token. Try again." : "El servidor rechazó el token. Prueba de nuevo.",
          app_outdated: lang === "en" ? "This app version can't receive notifications yet. Update the app from the App Store / Google Play and try again." : "Esta versión de la app aún no puede recibir notificaciones. Actualiza la app desde App Store / Google Play y vuelve a intentarlo.",
          native_error: lang === "en" ? "The phone couldn't register for notifications. Check that notifications are allowed for Smile & Shine in your phone settings." : "El móvil no pudo registrarse para notificaciones. Revisa que estén permitidas para Smile & Shine en los ajustes del teléfono.",
          no_token_timeout: lang === "en" ? "The phone took too long to respond. Try again." : "El móvil tardó demasiado en responder. Vuelve a intentarlo.",
          capacitor_missing: lang === "en" ? "Push on desktop is handled by your browser — try again after allowing the browser permission popup." : "En escritorio las notificaciones las gestiona el navegador — vuelve a intentarlo y acepta el aviso de permiso del navegador.",
        };
        setPushMsg(reasons[res.reason] || `${lang === "en" ? "Could not enable notifications." : "No se pudieron activar."} (${res.reason || "unknown"})`);
      }
    } catch (ex) {
      setPushMsg(`${lang === "en" ? "Error:" : "Error:"} ${ex?.message || ex}`);
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    setPushBusy(true); setPushMsg("");
    try {
      if (!pushHasToken) {
        setPushMsg(lang === "en"
          ? "This device has no token registered yet. Press \"Activate\" first."
          : "Este dispositivo aún no tiene token registrado. Pulsa primero \"Activar\".");
        return;
      }
      const { data } = await api.post("/push/test");
      if (data?.skipped) {
        setPushMsg(lang === "en" ? "Push disabled on server (missing Firebase key)." : "Push desactivado en el servidor (falta clave Firebase).");
      } else if (data?.no_tokens || data?.success === 0) {
        setPushMsg(lang === "en" ? "No devices registered on backend. Re-register first." : "No hay dispositivos registrados en el servidor. Re-registra primero.");
      } else {
        setPushMsg(lang === "en" ? `✓ Sent to ${data.success} device(s)` : `✓ Enviado a ${data.success} dispositivo(s)`);
      }
    } catch (ex) {
      setPushMsg(`${lang === "en" ? "Test failed:" : "Test fallido:"} ${ex?.response?.data?.detail || ex?.message || ""}`);
    } finally {
      setPushBusy(false);
    }
  };

  const doLogout = () => {
    logout();
    nav("/login");
  };

  const gestion = [
    { to: "/calendario", Icon: Calendar, label: t("perfil.gestion.agenda"), sub: t("perfil.gestion.agenda.sub"), bg: "#FFE6D7", color: "#C2410C" },
    { to: "/gestion/clientes", Icon: Users, label: t("perfil.gestion.clientes"), sub: t("perfil.gestion.clientes.sub"), bg: "#E8F1FF", color: "#2D62B7" },
    { to: "/gestion/servicios", Icon: Wrench, label: t("perfil.gestion.servicios"), sub: t("perfil.gestion.servicios.sub"), bg: "#FFE6D7", color: "#C2410C" },
    { to: "/gestion/embarcaciones", Icon: Ship, label: t("perfil.gestion.embarcaciones"), sub: t("perfil.gestion.embarcaciones.sub"), bg: "#E0F2FE", color: "#0369A1" },
    { to: "/gestion/usuarios", Icon: UserCircle, label: t("perfil.gestion.usuarios"), sub: t("perfil.gestion.usuarios.sub"), bg: "#EEF0F4", color: "#475569" },
    { to: "/gestion/albaranes", Icon: BarChart3, label: t("perfil.gestion.informes"), sub: t("perfil.gestion.informes.sub"), bg: "#D5F5E0", color: "#116A34" },
    { to: "/gestion/ajustes", Icon: SettingsIcon, label: t("settings.title"), sub: t("settings.empresa.title"), bg: "#EDE9FE", color: "#7C3AED" },
  ];

  return (
    <div className="px-5 pt-8 sns-fade-up" data-testid="page-perfil">
      <div className="flex items-center justify-between mb-6">
        <h1 className="sns-title text-3xl">{t("perfil.title")}</h1>
        {/* Selector idioma rápido también en perfil (mobile) */}
        <div className="inline-flex bg-white rounded-full p-1 border border-[var(--border)] shadow-sm" data-testid="perfil-lang-switch">
          <button
            onClick={() => setLang("es")}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${lang === "es" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
            data-testid="perfil-lang-es"
          >🇪🇸 ES</button>
          <button
            onClick={() => setLang("en")}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${lang === "en" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
            data-testid="perfil-lang-en"
          >🇬🇧 EN</button>
        </div>
      </div>

      {/* Profile card */}
      <div className="sns-card p-6 mb-8 text-center">
        <img
          src={user?.avatar_url || `${AVATAR_FALLBACK}${encodeURIComponent(user?.name || "U")}`}
          alt={user?.name}
          className="w-28 h-28 rounded-full object-cover mx-auto mb-4 ring-4 ring-[var(--coral)]/15"
          data-testid="profile-avatar"
        />
        <h2 className="sns-title text-2xl" data-testid="profile-name">{user?.name}</h2>
        <p className="text-slate-500 text-sm mb-4" data-testid="profile-email">{user?.email}</p>
        <div className="inline-flex items-center gap-1.5 bg-[var(--coral)] text-white font-bold text-xs px-4 py-2 rounded-full mb-3">
          <Shield size={12} /> {isAdmin ? t("perfil.role.admin") : t("perfil.role.empleado")}
        </div>
        {isAdmin && user?.rate_per_hour > 0 && (
          <div className="text-slate-600 text-sm flex items-center justify-center gap-1.5">
            <Euro size={14} /> {t("perfil.rate")} <strong>{user.rate_per_hour}{t("perfil.rate.per_hour")}</strong>
          </div>
        )}
      </div>

      {/* Mis horas - visible for everyone */}
      <div className="text-xs font-bold text-slate-400 tracking-wider mb-3 px-2">{t("perfil.section.time")}</div>
      <div className="sns-card mb-6">
        <Link
          to="/mis-horas"
          className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
          data-testid="link-mis-horas"
        >
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#FFE6D7", color: "#C2410C" }}>
            <Clock size={20} strokeWidth={2.3} />
          </div>
          <div className="flex-1">
            <div className="font-bold">{isAdmin ? t("perfil.team_hours") : t("perfil.my_hours")}</div>
            <div className="text-xs text-slate-500">{t("perfil.hours.sub")}</div>
          </div>
          <span className="text-slate-300 text-xl">›</span>
        </Link>
        {isAdmin && (
          <Link
            to="/plan"
            className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors border-t border-[var(--border)]"
            data-testid="link-plan"
          >
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#EDE9FE", color: "#7C3AED" }}>
              <Shield size={20} strokeWidth={2.3} />
            </div>
            <div className="flex-1">
              <div className="font-bold">{t("plan.title")}</div>
              <div className="text-xs text-slate-500">Free · Pro · Uso y facturación</div>
            </div>
            <span className="text-slate-300 text-xl">›</span>
          </Link>
        )}
      </div>

      {isAdmin && (
        <>
          <div className="text-xs font-bold text-slate-400 tracking-wider mb-3 px-2">{t("perfil.section.management")}</div>
          <div className="sns-card divide-y divide-[var(--border)] mb-6">
            {gestion.map((g) => (
              <Link
                key={g.to}
                to={g.to}
                className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                data-testid={`gestion-${g.label.toLowerCase()}`}
              >
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: g.bg, color: g.color }}>
                  <g.Icon size={20} strokeWidth={2.3} />
                </div>
                <div className="flex-1">
                  <div className="font-bold">{g.label}</div>
                  <div className="text-xs text-slate-500">{g.sub}</div>
                </div>
                <span className="text-slate-300 text-xl">›</span>
              </Link>
            ))}
          </div>
        </>
      )}

      <button
        onClick={doLogout}
        className="sns-card w-full px-5 py-4 flex items-center gap-3 text-red-600 hover:bg-red-50 transition-colors"
        data-testid="btn-logout"
      >
        <LogOut size={18} /> {t("nav.logout")}
      </button>

      {/* --- Notificaciones push --- */}
      <div className="text-xs font-bold text-slate-400 tracking-wider mt-8 mb-3 px-2">
        {lang === "en" ? "NOTIFICATIONS" : "NOTIFICACIONES"}
      </div>
      <div className="sns-card p-5 space-y-3" data-testid="push-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#FFE6D7] text-[#C2410C] flex items-center justify-center shrink-0">
            {pushPermission === "granted" && pushHasToken ? <Bell size={20} /> : <BellOff size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold">
              {lang === "en" ? "Push notifications" : "Notificaciones push"}
            </div>
            <div className="text-xs text-slate-500">
              {!pushEnabled
                ? (lang === "en" ? "Server not configured yet." : "Servidor aún no configurado.")
                : pushPermission === "denied"
                  ? (lang === "en" ? "Blocked in browser." : "Bloqueadas en el navegador.")
                  : pushPermission === "granted" && pushHasToken
                    ? (lang === "en" ? "Active on this device." : "Activas en este dispositivo.")
                    : (lang === "en" ? "Not activated on this device." : "No activadas en este dispositivo.")}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={activarPush}
            disabled={pushBusy}
            className="sns-btn sns-btn-accent disabled:opacity-50"
            data-testid="btn-activate-push"
          >
            {pushBusy
              ? (lang === "en" ? "..." : "...")
              : pushPermission === "granted" && pushHasToken
                ? (lang === "en" ? "Re-register" : "Re-registrar")
                : (lang === "en" ? "Enable" : "Activar")}
          </button>
          <button
            onClick={testPush}
            disabled={pushBusy}
            className="sns-btn sns-btn-ghost bg-white border border-[var(--border)] disabled:opacity-50"
            data-testid="btn-test-push"
          >
            {pushBusy ? "..." : (lang === "en" ? "Send test" : "Enviar prueba")}
          </button>
        </div>
        {pushMsg && (
          <p className="text-sm text-slate-700 bg-blue-50 border border-blue-100 rounded-xl p-3 font-medium" data-testid="push-msg">{pushMsg}</p>
        )}
        {isAdmin && (
          <p className="text-[10px] text-slate-400 leading-relaxed">
            {lang === "en"
              ? "Tip: employees receive notifications on job assignment, 1h reminder, reschedule, unassign and urgent notes (word \"urgente\" in the admin notes)."
              : "Tip: los empleados reciben notificaciones al asignar un trabajo, 1 h antes, al cambiar fecha/hora, al desasignar y cuando incluyes la palabra \"urgente\" en las observaciones."}
          </p>
        )}
      </div>
    </div>
  );
}
